import { Injectable } from '@angular/core';
import { ApiClass } from '@data/class/api-class';
import { environment } from '@env/environment';
import { Observable, catchError, map, of } from 'rxjs';

/**
 * Response del upload de audio
 */
export interface IUploadAudioResponse {
  audioUrl: string;
  fileName: string;
}

/**
 * Registro de llamada automatizada
 */
export interface ICallRecord {
  _id: string;
  campaignId?: string;
  strategyId?: string;
  leaseId: string;
  clientId: string;
  phone: string;
  callType: 'TTS' | 'AUDIO';
  status: 'PENDING' | 'SENT' | 'ANSWERED' | 'NO_ANSWER' | 'BUSY' | 'FAILED';
  message?: string;
  audioUrl?: string;
  dialerResponse?: Record<string, any>;
  sentAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Servicio API para gestión de llamadas automatizadas del módulo Dripping
 * Maneja el upload de audios y consulta de registros de llamadas
 */
@Injectable({
  providedIn: 'root'
})
export class ApiCallsService extends ApiClass {

  constructor() {
    super();
    this.path = 'dripping/calls';
  }

  /**
   * Sube un archivo de audio a Azure Blob Storage
   * @param file - Archivo de audio (MP3, WAV)
   * @returns Observable con URL del audio y nombre del archivo
   */
  uploadAudio(file: File): Observable<{ error: boolean; msg: string; data: IUploadAudioResponse | null }> {
    const response = { error: true, msg: '', data: null as IUploadAudioResponse | null };
    const formData = new FormData();
    formData.append('audio', file);
    
    return this.http.post<{ data: IUploadAudioResponse; error: boolean; msg: string }>(
      `${environment.apiUrl}${this.path}/upload-audio`, 
      formData
    ).pipe(
      map((r) => {
        response.error = r.error ?? false;
        response.data = r.data;
        response.msg = r.msg ?? 'Audio subido correctamente';
        return response;
      }),
      catchError((e) => {
        response.msg = e.error?.msg ?? 'Error al subir el audio';
        response.error = true;
        return of(response);
      })
    );
  }

  /**
   * Obtiene registros de llamadas por lease
   * @param leaseId - ID del lease
   * @returns Observable con array de registros de llamadas
   */
  getRecordsByLease(leaseId: string): Observable<{ error: boolean; msg: string; data: ICallRecord[] | null }> {
    const response = { error: true, msg: '', data: null as ICallRecord[] | null };
    
    return this.http.get<{ data: ICallRecord[]; error: boolean; msg: string }>(
      `${environment.apiUrl}${this.path}/records/lease/${leaseId}`
    ).pipe(
      map((r) => {
        response.error = r.error ?? false;
        response.data = r.data;
        response.msg = r.msg ?? '';
        return response;
      }),
      catchError((e) => {
        response.msg = e.error?.msg ?? 'Error al obtener registros';
        response.error = true;
        return of(response);
      })
    );
  }

  /**
   * Obtiene registros de llamadas por campaña
   * @param campaignId - ID de la campaña
   * @returns Observable con array de registros de llamadas
   */
  getRecordsByCampaign(campaignId: string): Observable<{ error: boolean; msg: string; data: ICallRecord[] | null }> {
    const response = { error: true, msg: '', data: null as ICallRecord[] | null };
    
    return this.http.get<{ data: ICallRecord[]; error: boolean; msg: string }>(
      `${environment.apiUrl}${this.path}/records/campaign/${campaignId}`
    ).pipe(
      map((r) => {
        response.error = r.error ?? false;
        response.data = r.data;
        response.msg = r.msg ?? '';
        return response;
      }),
      catchError((e) => {
        response.msg = e.error?.msg ?? 'Error al obtener registros';
        response.error = true;
        return of(response);
      })
    );
  }

  /**
   * Obtiene un registro de llamada por ID
   * @param id - ID del registro
   * @returns Observable con el registro de llamada
   */
  getRecord(id: string): Observable<{ error: boolean; msg: string; data: ICallRecord | null }> {
    const response = { error: true, msg: '', data: null as ICallRecord | null };
    
    return this.http.get<{ data: ICallRecord; error: boolean; msg: string }>(
      `${environment.apiUrl}${this.path}/records/${id}`
    ).pipe(
      map((r) => {
        response.error = r.error ?? false;
        response.data = r.data;
        response.msg = r.msg ?? '';
        return response;
      }),
      catchError((e) => {
        response.msg = e.error?.msg ?? 'Error al obtener registro';
        response.error = true;
        return of(response);
      })
    );
  }
}
